import { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface AIProcessorProps {
  images: File[];
  onComplete: (enhancedUrls: string[]) => void;
}

const STEPS = [
  { label: "Detecting dress area", duration: 600 },
  { label: "Segmenting foreground", duration: 700 },
  { label: "Removing background & model", duration: 800 },
  { label: "Reconstructing inner details", duration: 900 },
  { label: "Applying ghost mannequin effect", duration: 700 },
  { label: "Normalizing lighting & shadows", duration: 500 },
  { label: "Exporting enhanced images", duration: 400 },
];

export function AIProcessor({ images, onComplete }: AIProcessorProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let step = 0;
    let elapsed = 0;
    const total = STEPS.reduce((s, x) => s + x.duration, 0);

    const runStep = () => {
      if (step >= STEPS.length) {
        setProgress(100);
        setDone(true);
        const enhanced = images.map(f => URL.createObjectURL(f));
        setTimeout(() => onComplete(enhanced), 500);
        return;
      }
      setCurrentStep(step);
      const stepTime = STEPS[step].duration;
      const stepStart = elapsed;

      let innerElapsed = 0;
      const tick = setInterval(() => {
        innerElapsed += 50;
        const pct = Math.min(((stepStart + innerElapsed) / total) * 100, ((stepStart + stepTime) / total) * 100);
        setProgress(pct);
        if (innerElapsed >= stepTime) {
          clearInterval(tick);
          elapsed += stepTime;
          step++;
          runStep();
        }
      }, 50);
    };

    runStep();
  }, []);

  return (
    <div className="rounded-2xl border border-border/60 bg-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--rose-light)" }}>
          <Sparkles size={18} style={{ color: "var(--rose)" }} />
        </div>
        <div>
          <h3 className="font-semibold text-sm">AI Enhancement In Progress</h3>
          <p className="text-xs text-muted-foreground">Creating professional product images…</p>
        </div>
      </div>

      <Progress value={progress} className="h-2 mb-5" />

      <div className="space-y-2">
        {STEPS.map((step, i) => (
          <div key={i} className={`flex items-center gap-2.5 text-sm transition-all duration-300 ${
            i < currentStep ? "text-muted-foreground" : i === currentStep ? "text-foreground" : "text-muted-foreground/40"
          }`}>
            {i < currentStep ? (
              <CheckCircle2 size={14} style={{ color: "var(--rose)" }} className="flex-shrink-0" />
            ) : i === currentStep ? (
              <Loader2 size={14} className="flex-shrink-0 animate-spin" style={{ color: "var(--rose)" }} />
            ) : (
              <div className="w-3.5 h-3.5 rounded-full border border-border flex-shrink-0" />
            )}
            {step.label}
          </div>
        ))}
      </div>

      {done && (
        <div className="mt-4 p-3 rounded-xl text-sm font-medium text-center" style={{ background: "var(--rose-light)", color: "var(--rose)" }}>
          ✨ Enhancement complete! Your dress is ready.
        </div>
      )}
    </div>
  );
}
